import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { ThumbsUp, ThumbsDown } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

export function PapSmearScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const theme = useMedicalHistoryTheme();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();

  const selection = useMemo(() => {
    if (state.hadPapSmear == null) return null;
    return state.hadPapSmear ? "yes" : "no";
  }, [state.hadPapSmear]);

  const abnormalResultSelection = useMemo(() => {
    if (state.papSmearResultAbnormal == null) return null;
    return state.papSmearResultAbnormal ? "yes" : "no";
  }, [state.papSmearResultAbnormal]);

  const clearProcedureData = () => {
    const idsToDelete = state.papSmearProcedures
      .filter((p) => p.id)
      .map((p) => p.id!);
    updateState({
      hadPapSmearProcedures: null,
      papSmearProcedures: [],
      procedureIdsToDelete: [...state.procedureIdsToDelete, ...idsToDelete],
    });
  };

  const handleChange = (value: "yes" | "no") => {
    if (value === "no") {
      const idsToDelete = state.papSmearProcedures
        .filter((p) => p.id)
        .map((p) => p.id!);
      updateState({
        hadPapSmear: false,
        hadPapSmearProcedures: null,
        papSmearResultAbnormal: null,
        papSmearProcedures: [],
        procedureIdsToDelete: [...state.procedureIdsToDelete, ...idsToDelete],
      });
    } else {
      updateState({ hadPapSmear: true });
    }
  };

  const handleAbnormalResultChange = (value: "yes" | "no") => {
    if (value === "no") {
      clearProcedureData();
    }
    updateState({
      papSmearResultAbnormal: value === "yes",
      ...(value === "no" ? { hadPapSmearProcedures: null } : {}),
    });
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("PapSmear");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.pap_smear_title")}
      subtitle={t("med_history.gynae.sexual_health.pap_smear_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={
        selection === null ||
        (selection === "yes" && abnormalResultSelection === null)
      }
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.optionsContainer}>
          <SquareOptionButton
            icon={ThumbsUp}
            label={t("med_history.shared.yes")}
            isSelected={selection === "yes"}
            onPress={() => handleChange("yes")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
          <SquareOptionButton
            icon={ThumbsDown}
            label={t("med_history.shared.no")}
            isSelected={selection === "no"}
            onPress={() => handleChange("no")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
        </View>

        {selection === "yes" && (
          <View style={styles.followUp}>
            <Text
              style={{
                color: theme.textMuted,
                fontSize: 15,
                fontWeight: "600",
                marginBottom: 6,
              }}
            >
              {t("med_history.gynae.sexual_health.pap_smear_result_title")}
            </Text>
            <Text
              style={{
                color: theme.textMuted,
                fontSize: 14,
                lineHeight: 20,
                marginBottom: 14,
              }}
            >
              {t("med_history.gynae.sexual_health.pap_smear_result_subtitle")}
            </Text>
            <View style={styles.resultOptions}>
              <SquareOptionButton
                icon={ThumbsUp}
                label={t("med_history.shared.yes")}
                isSelected={abnormalResultSelection === "yes"}
                onPress={() => handleAbnormalResultChange("yes")}
                iconSize={22}
                containerSize={44}
                labelSize="lg"
              />
              <SquareOptionButton
                icon={ThumbsDown}
                label={t("med_history.shared.no")}
                isSelected={abnormalResultSelection === "no"}
                onPress={() => handleAbnormalResultChange("no")}
                iconSize={22}
                containerSize={44}
                labelSize="lg"
              />
            </View>
          </View>
        )}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  optionsContainer: {
    flexDirection: "row",
    gap: 12,
    marginBottom: 24,
    width: "100%",
  },
  followUp: {
    width: "100%",
    marginBottom: 16,
  },
  resultOptions: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
});
